import React, { useEffect, useState } from 'react';
import { Table, Button, message, Tag, Input } from 'antd';
import { SearchOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import { useBotAdmin } from '../../contexts/BotAdminContext';
import { useBotContacts } from '../../hooks/useWpbot';
import useAuth from '../../hooks/useAuth';
import wpbotService from '../../services/wpbotService';
import BotSelector from './BotSelector';
import { TBotContact } from '../../models/wpbotModel';
import { formatDateTime } from '../../utils';

/**
 * Contactos registrados en el bot seleccionado, con acción de
 * bloquear/desbloquear (set-contact-blocked).
 */
const ViewBotContactsList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const { botAdmin } = useBotAdmin();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [searchText, setSearchText] = useState('');

  const botId = botAdmin.selectedBotId;
  const { data, isLoading, error, mutate } = useBotContacts(tokenData, { bot_id: botId, company: company.current?.uid });

  useEffect(() => {
    if (error) {
      message.error("Error al cargar los contactos del bot");
    }
  }, [error]);

  const canToggleBlock = user.permissions.actions.join().includes('wpbot.action.contact.toggle_block')

  const handleToggleBlock = async (contact: TBotContact) => {
    const res = await wpbotService.setContactBlocked(tokenData, {
      bot_id: botId,
      contact_id: contact.contact_id,
      is_blocked: !contact.is_blocked,
      company: company.current?.uid,
    })
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success(`Contacto ${contact.is_blocked ? 'desbloqueado' : 'bloqueado'}`)
    mutate()
  }

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (val: string) => val || '—'
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Estado de registro',
      dataIndex: 'registration_status',
      key: 'registration_status',
      render: (val: string) => {
        const status = (val || '').toLowerCase()
        if (status === 'complete' || status === 'registered') {
          return <Tag color="green">Registrado</Tag>
        }
        if (status === 'rejected') {
          return <Tag color="red">Rechazado</Tag>
        }
        if (status === 'pending') {
          return <Tag color="orange">Pendiente</Tag>
        }
        return <Tag>{val || '—'}</Tag>
      }
    },
    {
      title: 'Último mensaje',
      dataIndex: 'last_message_at',
      key: 'last_message_at',
      render: (val: string | null) => formatDateTime(val)
    },
    {
      title: 'Bloqueado',
      dataIndex: 'is_blocked',
      key: 'is_blocked',
      render: (val: boolean) => val ? <Tag color="red">Bloqueado</Tag> : <Tag color="green">Activo</Tag>
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: TBotContact) => (
        canToggleBlock && (
          <Button
            danger={!record.is_blocked}
            icon={record.is_blocked ? <CheckCircleOutlined /> : <StopOutlined />}
            onClick={() => handleToggleBlock(record)}
          >
            {record.is_blocked ? "Desbloquear" : "Bloquear"}
          </Button>
        )
      ),
    },
  ];

  const contacts: TBotContact[] = data?.status === 200 ? (data?.contacts || []) : []

  const filteredData = contacts.filter((contact) =>
    contact.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    contact.phone?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
        <h2>Contactos del bot</h2>
        <BotSelector />
      </div>

      <div style={{ marginBottom: '16px', maxWidth: '400px' }}>
        <Input
          placeholder="Buscar contacto (nombre, teléfono)..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: botId ? "Este bot no tiene contactos registrados" : "Selecciona un bot" }}
      />
    </div>
  );
};

export default ViewBotContactsList;
